/**
 * ConfluenceRenderer — structured rendering for Confluence pages and search results.
 */

import { FileText, User, Calendar, Eye, BookOpen } from 'lucide-react'
import { MessageContent } from '../MessageContent'
import { SupplementSections } from './SupplementSections'

interface ConfluencePage {
  id?: string
  title?: string
  body?: { storage?: { value?: string }; view?: { value?: string } } | string
  space?: { key?: string; name?: string } | string
  version?: { number?: number; when?: string; by?: { displayName?: string } } | number
  status?: string
  _links?: { webui?: string; self?: string }
  metadata?: Record<string, unknown>
  children?: { page?: { results?: unknown[] } }
  ancestors?: Array<{ title?: string }>
  [k: string]: unknown
}

function str(val: unknown): string {
  if (!val) return ''
  if (typeof val === 'string') return val
  if (typeof val === 'number') return String(val)
  if (typeof val === 'object' && val !== null) {
    if ('name' in val) return (val as { name?: string }).name || ''
    if ('key' in val) return (val as { key?: string }).key || ''
    if ('displayName' in val) return (val as { displayName?: string }).displayName || ''
  }
  return ''
}

function extractBody(page: ConfluencePage): string {
  if (!page.body) return ''
  if (typeof page.body === 'string') return page.body
  return page.body.view?.value || page.body.storage?.value || ''
}

// ---------------------------------------------------------------------------
// Single Page
// ---------------------------------------------------------------------------

export function ConfluencePageRenderer({ data }: { data: Record<string, unknown> }) {
  const page = data as unknown as ConfluencePage
  const space = str(page.space)
  const version = typeof page.version === 'number' ? page.version : page.version?.number
  const lastEditor = typeof page.version === 'object' ? str(page.version?.by) : ''
  const lastEdited = typeof page.version === 'object' ? page.version?.when : undefined
  const body = extractBody(page)

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <BookOpen className="w-4 h-4 text-info" />
          {space && (
            <span className="text-xs text-info-foreground bg-info-soft rounded px-1.5 py-0.5 font-medium">{space}</span>
          )}
          {version && (
            <span className="text-xs text-subtle-foreground">v{version}</span>
          )}
        </div>
        <h3 className="text-sm font-semibold text-foreground leading-snug">{page.title}</h3>
      </div>

      {/* Breadcrumb */}
      {page.ancestors && page.ancestors.length > 0 && (
        <div className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
          {page.ancestors.map((a, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <span className="text-subtle-foreground">/</span>}
              <span>{a.title}</span>
            </span>
          ))}
          <span className="text-subtle-foreground">/</span>
          <span className="text-foreground font-medium">{page.title}</span>
        </div>
      )}

      {/* Metadata */}
      <div className="grid grid-cols-2 gap-3">
        {lastEditor && (
          <div className="flex items-center gap-2 text-xs">
            <User className="w-3.5 h-3.5 text-subtle-foreground shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold">Last Editor</div>
              <div className="text-foreground">{lastEditor}</div>
            </div>
          </div>
        )}
        {lastEdited && (
          <div className="flex items-center gap-2 text-xs">
            <Calendar className="w-3.5 h-3.5 text-subtle-foreground shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold">Last Edited</div>
              <div className="text-foreground">{new Date(lastEdited).toLocaleDateString()}</div>
            </div>
          </div>
        )}
      </div>

      {/* Body Content */}
      {body && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5 flex items-center gap-1">
            <Eye className="w-3 h-3" /> Content
          </div>
          <div className="rounded-lg bg-foreground/[0.03] border border-subtle p-4 text-xs text-foreground leading-relaxed max-h-96 overflow-y-auto">
            <MessageContent content={body} />
          </div>
        </div>
      )}

      {/* Merged supplements (comments, attachments, versions) */}
      <SupplementSections data={data} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Multi-Page List
// ---------------------------------------------------------------------------

export function ConfluencePagesRenderer({ data }: { data: unknown[] }) {
  const pages = data as ConfluencePage[]

  return (
    <div className="space-y-2">
      {pages.map((page, i) => {
        const space = str(page.space)

        return (
          <div key={page.id || i} className="rounded-lg border border-subtle bg-foreground/[0.03] p-3 hover:border-info/30 transition">
            <div className="flex items-start gap-2">
              <FileText className="w-4 h-4 text-info shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-foreground font-medium line-clamp-1">{page.title}</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                  {space && <span className="bg-info-soft text-info-foreground rounded px-1.5 py-0.5">{space}</span>}
                  {page.status && <span>{page.status}</span>}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
